<template>
  <div class="CartProducts">
    <v-toolbar
      color="grey lighten-3"
      flat
    >
      <v-toolbar-title>
        <language-switcher
          v-if="receipt.language"
          :current-locale="receipt.language"
        />
        <language-switcher
          v-else
          :current-locale="$store.getters.locale"
        />
        {{ $t('Cart') }}
      </v-toolbar-title>
      <v-spacer />

      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary darken-1"
            class="elevation-0 mr-2"
            fab
            small
            v-on="on"
            @click="showProductSearchDialog = true"
          >
            <v-icon small>fal fa-search</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('Search {0}', [$t('Products')]) }}</span>
      </v-tooltip>

      <v-tooltip top>
        <template v-slot:activator="{ on: tooltip}">
          <div
            class="d-inline"
            v-on="tooltip"
          >
            <v-menu>
              <template v-slot:activator="{ on }">
                <v-btn
                  class="elevation-1"
                  color="black"
                  dark
                  fab
                  small
                  v-on="on"
                >
                  <v-icon size="15px">far fa-undo-alt</v-icon>
                </v-btn>
              </template>
              <v-list
                subheader
                two-line
              >
                <v-list-item
                  v-for="(item, index) in ReturnTypes"
                  :key="index"
                  @click="selectReturnType(item)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ $t(item.label) }}</v-list-item-title>
                    <v-list-item-subtitle>{{ $t(item.description) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
        <span>{{ $t('Return an item') }}</span>
      </v-tooltip>
    </v-toolbar>
    <v-divider />
    <v-data-table
      id="table-cart"
      :headers="headers"
      :items="products"
      :loading="$wait.is('fetching receipt')"
      class="elevation-1 white bt-0"
      disable-pagination
      hide-default-footer
    >
      <template v-slot:header.uuid>
        <v-tooltip left>
          <template v-slot:activator="{ on }">
            <v-btn
              v-show="itemCount"
              icon
              ripple
              small
              v-on="on"
              @click="clearProducts"
            >
              <v-icon size="14px">fal fa-trash-alt</v-icon>
            </v-btn>
          </template>
          <span>{{ helpers.capitalize($t('Remove {0}', [$t('All Items')])) }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item="props">
        <tr
          :class="{'active': activeRow === props.item}"
          class="lighten-5"
          @click="activateRow(props.item)"
        >
          <td>
            <v-btn
              :loading="$wait.is(`deleting row ${props.item.uuid}`)"
              class="ma-0"
              icon
              ripple
              small
              @click.stop="removeProduct(props.item.uuid)"
            >
              <v-icon small>fal fa-trash-alt</v-icon>
            </v-btn>
          </td>
          <td class="text-body-2 pa-2">{{ props.item.display }}
            <template v-if="props.item.returnType">
              <br>
              <v-alert
                class="pa-0 pl-2 pr-2 mt-1 mb-0 d-inline-block"
                color="blue-grey lighten-5"
                dense
                style="border: 1px solid #ccc !important"
              >
                <v-icon size="14px">far fa-undo-alt</v-icon>
                <small>
                  {{ `${$t(getReturnTypeField(props.item.returnType, 'label'))}: ${$t(getReturnTypeField(props.item.returnType, 'description')).toLowerCase()}` }}
                </small>
              </v-alert>
            </template>
          </td>
          <td class="text-body-2">
            <v-dialog
              v-model="showChangeQtyKeyboard[props.item.uuid]"
              eager
              max-width="350px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :disabled="itemIsSample(props.item)"
                  :value="props.item.qty"
                  class="pt-0 mt-0"
                  hide-details
                  readonly
                  single-line
                  v-on="on"
                  @focus="setQtyFocus(props.item.uuid)"
                />
              </template>
              <v-card class="grey lighten-5">
                <v-card-text class="pt-4">
                  <numeric-keyboard
                    :ref="`keyboardQty-${props.item.uuid}`"
                    :clear-input="showChangeQtyKeyboard[props.item.uuid]"
                    :keyboard-class="`keyboardQty-${props.item.uuid}`"
                    @clear="$set(keyboardQtys, props.item.uuid, null)"
                    @input="$set(keyboardQtys, props.item.uuid, $event)"
                  >
                    <v-text-field
                      slot="input"
                      slot-scope="{ setKeyboardInput }"
                      v-model="keyboardQtys[props.item.uuid]"
                      :ref="`inputQty-${props.item.uuid}`"
                      :label="$t('Quantity')"
                      autocomplete="off"
                      filled
                      hide-details
                      @input="setKeyboardInput($event)"
                      @keypress.enter="changeQuantity(props.item.uuid, keyboardQtys[props.item.uuid])"
                    />
                  </numeric-keyboard>
                </v-card-text>
                <v-divider />
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    :loading="$wait.is(`saving qty ${props.item.uuid}`)"
                    class="font-weight-bold"
                    color="primary"
                    text
                    @click="changeQuantity(props.item.uuid, keyboardQtys[props.item.uuid])"
                  >
                    {{ $t('Save') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </td>
          <td class="text-body-2">
            <v-dialog
              v-if="!props.item.discountType && !props.item.discountValue && !hasReceiptDiscount"
              v-model="showChangePriceKeyboard[props.item.uuid]"
              eager
              max-width="350px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :disabled="itemIsSample(props.item) || itemIsService(props.item.product)"
                  :value="showRowPrice(props.item)"
                  class="pt-0 mt-0"
                  hide-details
                  readonly
                  single-line
                  v-on="on"
                  @focus="setPriceFocus(props.item.uuid)"
                />
              </template>
              <v-card class="grey lighten-5">
                <v-card-text class="pt-4">
                  <numeric-keyboard
                    :ref="`keyboardPrice-${props.item.uuid}`"
                    :clear-input="showChangePriceKeyboard[props.item.uuid]"
                    :keyboard-class="`keyboardPrice-${props.item.uuid}`"
                    @input="$set(keyboardPrices, props.item.uuid, $event)"
                    @clear="$set(keyboardPrices, props.item.uuid, null)"
                  >
                    <v-text-field
                      v-comma
                      slot="input"
                      slot-scope="{ setKeyboardInput }"
                      :ref="`inputPrice-${props.item.uuid}`"
                      v-model="keyboardPrices[props.item.uuid]"
                      :label="$t('Amount')"
                      autocomplete="off"
                      filled
                      hide-details
                      @input="setKeyboardInput($event)"
                      @keypress.enter="changePrice(props.item.uuid, keyboardPrices[props.item.uuid])"
                    />
                  </numeric-keyboard>
                </v-card-text>
                <v-divider />
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    :loading="$wait.is(`saving price ${props.item.uuid}`)"
                    class="font-weight-bold"
                    color="primary"
                    text
                    @click="changePrice(props.item.uuid, keyboardPrices[props.item.uuid])"
                  >
                    {{ $t('Save') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-text-field
              v-else
              :value="props.item.priceInclVat | money"
              class="pt-0 mt-0"
              disabled
              hide-details
              single-line
              @focus="$event.target.select()"
              @input="changePriceDebounced(props.item.uuid, $event)"
            />
          </td>
          <td class="text-body-2">
            {{ props.item.totalInclVat | money }}
            <div
              v-show="props.item.totalInclVat !== props.item.totalOriginalInclVat"
              class="original-price"
            >
              {{ props.item.totalOriginalInclVat | money }}
            </div>
          </td>
          <td class="text-right">
            <template v-if="!isReturn(props.item)">
              <v-tooltip
                v-if="!itemIsSample(props.item) && itemIsProduct(props.item.product)"
                bottom
              >
                <template v-slot:activator="{ on: tooltip }">
                  <div
                    class="d-inline"
                    v-on="tooltip"
                  >
                    <v-dialog
                      :ref="`discountDialog-${props.item.uuid}`"
                      v-model="showChangeDiscountKeyboard[props.item.uuid]"
                      :key="props.item.uuid"
                      :max-width="450"
                      eager
                    >
                      <template v-slot:activator="{ on }">
                        <v-btn
                          :disabled="hasReceiptDiscount"
                          class="elevation-0"
                          fab
                          icon
                          small
                          v-on="on"
                        >
                          <v-icon
                            v-if="itemHasDiscount(props.item)"
                            color="primary"
                          >fa-badge-percent</v-icon>
                          <v-icon v-else>fal fa-badge-percent</v-icon>
                        </v-btn>
                      </template>
                      <forms-discounts-receipt-product-save
                        v-if="showChangeDiscountKeyboard[props.item.uuid]"
                        :ref="`form-row-discount-${props.item.uuid}`"
                        :form-title="$t('Row Discount')"
                        :keyboard-class="`keyboardReceiptProductDiscount-${props.item.uuid}`"
                        :receipt-product-object="props.item"
                        @saved="hideDiscountKeyboard(props.item.uuid)"
                      />
                    </v-dialog>
                  </div>
                </template>
                <span v-show="hasReceiptDiscount">{{ $t('You Have Already Applied A Receipt Discount.') }}</span>
                <span v-show="!hasReceiptDiscount">{{ $t('Give {0}', [$t('Row Discount').toLowerCase()]) }}</span>
              </v-tooltip>

              <v-tooltip
                v-if="cashRegister.store.samplesEnabled && itemIsProduct(props.item.product)"
                bottom
              >
                <template v-slot:activator="{ on: tooltip }">
                  <div
                    class="d-inline"
                    v-on="tooltip"
                  >
                    <v-dialog
                      v-if="!itemIsSample(props.item)"
                      v-model="showChangeSamplesSlider[props.item.uuid]"
                      :max-width="450"
                      eager
                    >
                      <template v-slot:activator="{ on }">
                        <v-btn
                          class="elevation-0"
                          fab
                          icon
                          small
                          v-on="on"
                          @click="setSamplesFocus(props.item.uuid)"
                        >
                          <v-icon>fal fa-gift</v-icon>
                        </v-btn>
                      </template>
                      <forms-samples-save
                        :ref="`form-samples-${props.item.uuid}`"
                        :clear-input="showChangeSamplesSlider[props.item.uuid]"
                        :key="`${props.item.uuid}-slider`"
                        :receipt-product-object="props.item"
                        @saved="$set(showChangeSamplesSlider, $event.uuid, false)"
                      />
                    </v-dialog>
                  </div>
                </template>
                <span>{{ $t('Give {0}', [$t('Free Samples').toLowerCase()]) }}</span>
              </v-tooltip>
            </template>
          </td>
        </tr>
      </template>
      <template slot="no-data">
        <span class="subheading">{{ $t('The Cart Is Empty.') }}</span>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showProductSearchDialog"
      eager
      fullscreen
      hide-overlay
    >
      <v-card
        v-if="showProductSearchDialog"
        tile
      >
        <v-app-bar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>{{ $t('Search {0}', [$t('Product')]) }}</v-toolbar-title>
          <v-spacer />
          <catalog-switcher class="mr-5" />
          <v-toolbar-items>
            <v-divider vertical />
            <v-btn
              dark
              icon
              @click.native="showProductSearchDialog = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-app-bar>
        <v-card-text class="pt-4">
          <products-table
            ref="productSearchList"
            :focus-input="showProductSearchDialog"
            :init-barcode-scanner="showProductSearchDialog"
            :return-type="chosenReturnType"
            include-search
            @added-to-cart="addedToCart($event)"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="showProductChooserDialog"
      v-model="showProductChooserDialog"
      :width="850"
    >
      <v-card tile>
        <v-app-bar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>{{ $t('Select {0}', [$t('Product')]) }}</v-toolbar-title>
          <v-spacer />

          <v-toolbar-items>
            <v-btn
              dark
              icon
              @click.native="showProductChooserDialog = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-app-bar>
        <v-card-text class="pt-4">
          <v-alert
            class="d-inline-block"
            color="orange"
            text
          >
            <v-icon
              class="mr-2"
              color="orange"
            >fal fa-exclamation-triangle</v-icon>
            {{ $t('Multiple products found for barcode: "{0}".', [scannedBarcode]) }} {{ $t('Select the correct product below.') }}
          </v-alert>
          <product-chooser
            :barcode="scannedBarcode"
            @item-selected="selectProduct"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <select-tax-stamp-dialog
      :show-dialog="showTaxStampDialog"
      :item="activeProduct"
      @item-selected="selectTaxStamp($event)"
      @closed="showTaxStampDialog = false" />
    <v-fab-transition>
      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-btn
            v-show="!showProductSearchDialog"
            bottom
            color="secondary darken-1"
            dark
            fab
            fixed
            right
            v-on="on"
            @click="showProductSearchDialog = true"
          >
            <v-icon size="22px">fal fa-search</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('Search {0}', [$t('Products')]) }}</span>
      </v-tooltip>
    </v-fab-transition>

    <barcode-scanner
      v-if="allowScanning"
      class-name="CartProducts"
      @scan-init="scanProduct($event)"
    />
    <keyboard-listener
      v-if="allowHotkeys"
      ref="keyboardListener"
      :keycode-end="13"
      :keycode-start="106"
      @ended="checkKeyboardInput"
    />
  </div>
</template>

<script>
import FormsDiscountsReceiptProductSave from '@/components/forms/discounts/FormsDiscountsReceiptProductSave'
import FormsSamplesSave from '@/components/forms/discounts/FormsSamplesSave'
import NumericKeyboard from '@/components/NumericKeyboard'
import ProductChooser from '@/components/products/ProductChooser'
import ProductsTable from '@/components/products/ProductsTable'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import BarcodeScanner from '@/components/BarcodeScanner'
import CatalogSwitcher from '@/components/CatalogSwitcher'
import KeyboardListener from '@/components/KeyboardListener'
import SelectTaxStampDialog from '@/components/dialogs/SelectTaxStampDialog'
import productService from '@/services/products'
import { Types as ReturnTypes, getLabel as getReturnTypeLabel, getDescription as getReturnTypeDescription } from '@/utils/types/return'
import productTypeMixin from '@/utils/mixins/productTypeMixin'
import { RECEIPT_PRODUCT_SAMPLE } from '@/utils/types/receipt-product'
import _ from 'lodash'
import NumberInputSpinner from 'vue-number-input-spinner'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'CartProducts',

  components: {
    BarcodeScanner,
    CatalogSwitcher,
    FormsDiscountsReceiptProductSave,
    FormsSamplesSave,
    KeyboardListener,
    LanguageSwitcher,
    NumberInputSpinner,
    NumericKeyboard,
    ProductChooser,
    ProductsTable,
    SelectTaxStampDialog
  },

  mixins: [ productTypeMixin ],

  props: {
    propAllowHotkeys: {
      type: Boolean,
      required: false,
      default: true
    },
    propAllowScanning: {
      type: Boolean,
      required: false,
      default: true
    }
  },

  data () {
    return {
      headers: [
        {
          text: '',
          value: 'uuid',
          sortable: false,
          width: '20px'
        },
        {
          text: this.$t('Name'),
          value: 'name',
          sortable: false
        },
        {
          text: this.$t('Quantity'),
          value: 'qty',
          sortable: false,
          width: '100px'
        },
        {
          text: this.$t('Unit price'),
          value: 'price',
          sortable: false,
          width: '150px'
        },
        {
          text: this.$t('Subtotal'),
          value: 'subtotal',
          sortable: false,
          width: '100px'
        },
        {
          text: '',
          value: 'name',
          sortable: false
        }
      ],
      showChangePriceKeyboard: {},
      showChangeQtyKeyboard: {},
      showChangeDiscountKeyboard: {},
      showChangeSamplesSlider: {},
      keyboardPrices: {},
      keyboardQtys: {},
      productsWithDuplicateBarcodes: null,
      showProductSearchDialog: false,
      showProductChooserDialog: false,
      showTaxStampDialog: false,
      chosenReturnType: null,
      ReturnTypes,
      activeRow: null,
      activeProduct: null,
      allowScanning: this.propAllowScanning,
      allowHotkeys: this.propAllowHotkeys,
      scannedBarcode: null,
      selectedTaxStamp: null
    }
  },

  computed: {
    ...mapGetters('RECEIPT', ['products', 'itemCount', 'productExistsInReceipt']),
    ...mapState('RECEIPT', ['receipt']),
    ...mapState(['cashRegister']),
    hasReceiptDiscount () {
      return !!(this.receipt.discountType && this.receipt.discountValue)
    }
  },

  watch: {
    products (val) {
      this.activateLastRow()
    },
    propAllowScanning (val) {
      this.allowScanning = val
    },
    propAllowHotkeys (val) {
      this.allowHotkeys = val
    },
    showProductSearchDialog (val) {
      this.allowScanning = !val
      this.$emit('cart-blurred', val)
      // Revert return type to default when productsearch has been closed
      if (!val) {
        this.chosenReturnType = null
      }
    }
  },

  methods: {
    activateLastRow () {
      const lastRow = _.last(this.products)
      if (lastRow) {
        this.activateRow(lastRow)
      } else this.activateRow(null)
    },
    activateRow (row) {
      this.activeRow = row
    },
    addedToCart (item) {
      this.activateRow(item.receiptProduct)
      this.showProductSearchDialog = false
      this.showProductChooserDialog = false
      this.showTaxStampDialog = false
    },
    changePrice (receiptProductUuid, price) {
      this.$wait.start(`saving price ${receiptProductUuid}`)
      return this.$store.dispatch('RECEIPT/CHANGE_PRODUCT_PRICE', { receiptProductUuid, price: parseFloat(price) })
        .then(data => {
          this.hidePriceKeyboard(receiptProductUuid)
          this.helpers.toastSuccess(data.message)
        })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally(() => this.$wait.end(`saving price ${receiptProductUuid}`))
    },
    changePriceDebounced: _.debounce(function (receiptProductUuid, price) {
      this.changePrice(receiptProductUuid, price)
    }, 750),
    changeQuantity (receiptProductUuid, quantity) {
      this.$wait.start(`saving qty ${receiptProductUuid}`)
      return this.$store.dispatch('RECEIPT/CHANGE_PRODUCT_QUANTITY', { receiptProductUuid, qty: parseInt(quantity) })
        .then(data => {
          this.hideQtyKeyboard(receiptProductUuid)
          this.helpers.toastSuccess(data.message)
        })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally(() => this.$wait.end(`saving qty ${receiptProductUuid}`))
    },
    changeQuantityDebounced: _.debounce(function (receiptProductUuid, quantity) {
      this.changeQuantity(receiptProductUuid, quantity)
    }, 750),
    checkKeyboardInput (value) {
      if (!this.activeRow || this.activeRow.saleType === RECEIPT_PRODUCT_SAMPLE) return false
      this.changeQuantity(this.activeRow.uuid, value)
    },
    clearProducts () {
      this.$swal({
        title: this.$t('Are you sure?'),
        type: 'warning',
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Yes'),
        showCancelButton: true,
        reverseButtons: true
      })
        .then((result) => {
          if (result.value) {
            return this.$store.dispatch('RECEIPT/CLEAR_PRODUCTS')
              .then(data => {
                this.helpers.toastSuccess(data.message)
                this.activateRow(null)
              })
              .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
          }
        })
    },
    getReturnTypeField (code, field) {
      if (field === 'label') return getReturnTypeLabel(code)
      if (field === 'description') return getReturnTypeDescription(code)
    },
    hideDiscountKeyboard (receiptProductUuid) {
      // this is actually not best practice ...
      this.$set(this.showChangeDiscountKeyboard, receiptProductUuid, false)
    },
    hidePriceKeyboard (receiptProductUuid) {
      this.$set(this.showChangePriceKeyboard, receiptProductUuid, false)
    },
    hideQtyKeyboard (receiptProductUuid) {
      this.$set(this.showChangeQtyKeyboard, receiptProductUuid, false)
    },
    isReturn (item) {
      return !!item.returnType
    },
    itemHasDiscount (item) {
      return (item.discountType && item.discountValue !== null)
    },
    itemIsSample (item) {
      return item.saleType === RECEIPT_PRODUCT_SAMPLE
    },
    removeProduct (uuid) {
      this.$wait.start(`deleting row ${uuid}`)
      this.activateRow(null)
      return this.$store.dispatch('RECEIPT/REMOVE_PRODUCT', uuid)
        .then(data => this.helpers.toastSuccess(data.message))
        .catch(data => { if (_.has(data, 'message')) this.helpers.toastError(data.message) })
        .finally(() => this.$wait.end(`deleting row ${uuid}`))
    },
    scanProduct (barcode) {
      this.scannedBarcode = barcode
      if (this.$refs.keyboardListener) this.$refs.keyboardListener.deactivate()
      this.activateRow(null)

      return productService.countByBarcode(barcode)
        .then(data => {
          if (data.data.count > 1) this.selectDuplicateProduct()
          else {
            productService.findByBarcode(barcode)
              .then(data => {
                this.activeProduct = data.data.product
                // Check if the product is already in cart
                if (!data.data.barcode.has_tax_stamp && data.data.product.retail_price_tax > 0 && !this.productExistsInReceipt(data.data.product.id)) {
                  this.showTaxStampDialog = true
                } else {
                  return this.$store.dispatch('RECEIPT/ADD_PRODUCT', { barcode, qty: 1 })
                    .then(data => {
                      this.activateRow(data.receiptProduct)
                      return this.helpers.toastSuccess(data.message)
                    })
                    .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
                }
              }).catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
          }
        })
    },
    selectDuplicateProduct () {
      this.showProductChooserDialog = true
    },
    selectProduct (item) {
      this.activeProduct = item
      if (item.retailPriceTax > 0 && !this.productExistsInReceipt(item.id)) {
        this.showTaxStampDialog = true
      } else {
        return this.$store.dispatch('RECEIPT/ADD_PRODUCT', { id: item.id, qty: 1, returnType: this.chosenReturnType })
          .then(data => {
            this.$wait.start(`${item.id} adding to cart`)
            this.$wait.start('selecting duplicate product')
            item.receiptProduct = data.receiptProduct
            this.helpers.toastSuccess(data.message)
            this.addedToCart(item)
          })
          .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
          .finally(() => {
            this.$wait.end(`${item.id} adding to cart`)
            this.$wait.end('selecting duplicate product')
          })
      }
    },
    selectReturnType (value) {
      this.chosenReturnType = value
      this.showProductSearchDialog = true
    },
    selectTaxStamp (value) {
      this.$wait.start(`${this.activeProduct.id} adding to cart`)
      this.$wait.start('selecting tax stamp product')
      return this.$store.dispatch('RECEIPT/ADD_PRODUCT', { id: this.activeProduct.id, qty: 1, returnType: this.chosenReturnType, has_tax_stamp: value })
        .then(data => {
          this.activeProduct.receiptProduct = data.receiptProduct
          this.helpers.toastSuccess(data.message)
          this.addedToCart(this.activeProduct)
        })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally(() => {
          this.$wait.end(`${this.activeProduct.id} adding to cart`)
          this.$wait.end('selecting tax stamp product')
        })
    },
    setPriceFocus (uuid) {
      setTimeout(() => { this.$nextTick(this.$refs[`inputPrice-${uuid}`].focus) }, 300)
      delete this.keyboardPrices[uuid]
    },
    setSamplesFocus (uuid) {
      setTimeout(() => { this.$nextTick(this.$refs[`form-samples-${uuid}`].$refs['input'].focus) }, 300)
      this.$refs[`form-samples-${uuid}`].$refs['keyboard'].clear()
    },
    setQtyFocus (uuid) {
      setTimeout(() => { this.$nextTick(this.$refs[`inputQty-${uuid}`].focus) }, 300)
      delete this.keyboardQtys[uuid]
    },
    showRowPrice (row) {
      return this.$options.filters.money(row.priceInclVat)
    }
  }
}
</script>

<style lang="scss" scoped>
.v-icon {
  font-size: 18px;
}
.original-price {
  font-size: 13px;
  text-decoration: line-through;
}
</style>



// WEBPACK FOOTER //
// src/components/checkout/CartProducts.vue